import React from "react";
// Relative on purpose: no "@" alias under Jest (cf. LanguageAwareField.jsx).
import { useEntitlements } from "../entitlements/EntitlementsContext";
import { CAP, UNLIMITED, isAtLimit } from "../entitlements/capabilities";

/** The two plan ceilings the practitioner can run into, in the order they matter. */
const METERS = [
  { key: CAP.bookingsLimit, labelKey: "bill.usage.bookings", testId: "usage-bookings" },
  { key: CAP.productsLimit, labelKey: "bill.usage.products", testId: "usage-products" },
];

/**
 * "How much of my plan have I used?" — `used / value` for each limit, from the capability
 * map (`value` is the snapshot's number, `used` the server's own count: bookings created
 * this UTC month that are not cancelled/expired, and every product row). Unlimited
 * (`-1`) is said in words, never as a number.
 *
 * A meter whose numbers the map does not carry is not drawn — "0 / 0" would be a claim.
 * Nothing at all is drawn while the map is loading or failed; the rest of Billing does
 * not depend on it.
 */
export default function PlanUsageCard({ t }) {
  const { limit } = useEntitlements();
  const meters = METERS.map((m) => ({ ...m, numbers: limit(m.key) })).filter((m) => m.numbers);
  if (meters.length === 0) return null;
  return (
    <div className="bg-white border border-eh-border-soft rounded-2xl p-6" data-testid="plan-usage">
      <h3 className="font-display text-xl text-eh-ink mb-4">{t("bill.usage.title")}</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        {meters.map(({ key, labelKey, testId, numbers: { value, used } }) => {
          const unlimited = value === UNLIMITED;
          const full = isAtLimit(value, used);
          const pct = unlimited || !Number.isInteger(used) || value === 0
            ? (full ? 100 : 0)
            : Math.min(100, Math.round((used / value) * 100));
          const shown = Number.isInteger(used) ? used : "—";
          return (
            <div key={key} data-testid={testId} className="bg-eh-cream/30 rounded-xl p-4">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-sm text-eh-ink">{t(labelKey)}</span>
                <span className="text-sm font-medium text-eh-ink" data-testid={`${testId}-value`}>
                  {unlimited
                    ? t("bill.usage.usedUnlimited", { used: shown })
                    : t("bill.usage.ofLimit", { used: shown, limit: value })}
                </span>
              </div>
              {!unlimited && (
                <div
                  className="mt-2 h-1.5 rounded-full bg-eh-border-soft overflow-hidden"
                  role="meter"
                  aria-valuemin={0}
                  aria-valuemax={value}
                  aria-valuenow={Number.isInteger(used) ? Math.min(used, value) : undefined}
                  aria-label={t(labelKey)}
                >
                  <div className={`h-full ${full ? "bg-rose-500" : "bg-eh-sage-deep"}`} style={{ width: `${pct}%` }} />
                </div>
              )}
              {full && (
                <div className="mt-2 text-xs text-rose-700" data-testid={`${testId}-full`}>{t("bill.usage.atLimit")}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
